// Lógica pura de la importación masiva de sedes y trabajadores:
// cabeceras, conversión de filas, diagnóstico de columnas, resolución de
// sedes y detección de duplicados. Sin React ni llamadas a la API, para
// poder cubrirla con tests. El modal (components/BulkImportModal.js) solo
// orquesta UI + API.
import { DAYS_OF_WEEK } from '../services/api';
import { USER_TYPE_LABELS } from '../lib/permissions';
import { isPasswordValid, firstFailureMessage, PASSWORD_MIN_LENGTH } from '../lib/passwordStrength';

// Quita acentos Y la ñ (NFD descompone ñ en n + ~ y la regex se lleva el ~),
// pasa a minúsculas y colapsa espacios.
export const norm = (s) => String(s ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[\s ​]+/g, ' ')
  .toLowerCase()
  .trim();

// Aplica norm() a todas las claves de un mapa. Así las claves "naturales"
// con acento ('teléfono', 'contraseña'…) dejan de ser código muerto.
const normalizeKeys = (map) => {
  const out = {};
  for (const [k, v] of Object.entries(map)) out[norm(k)] = v;
  return out;
};

// ─────────────────────────────────────────────────────────────
// Cabeceras en español → campos internos
// ─────────────────────────────────────────────────────────────
export const SEDE_HEADERS_ES = ['Nombre de la sede', 'Email', 'Teléfono', 'Login habilitado'];

export const SEDE_HEADER_MAP = normalizeKeys({
  'nombre de la sede': 'name',
  'nombre': 'name',
  'sede': 'name',
  'email': 'email',
  'correo': 'email',
  'teléfono': 'phone',
  'login habilitado': 'isLoginEnabled',
});

const SEDE_FIELD_LABELS = {
  name: 'Nombre de la sede',
  email: 'Email',
  phone: 'Teléfono',
  isLoginEnabled: 'Login habilitado',
};

// 4 columnas por día (entrada/salida × mañana/tarde) × 7 días = 28 columnas
// de jornada. Etiquetas en lenguaje natural.
export const SCHEDULE_HEADERS_ES = DAYS_OF_WEEK.flatMap(({ label }) => [
  `${label} entrada mañana`,
  `${label} salida mañana`,
  `${label} entrada tarde`,
  `${label} salida tarde`,
]);

export const WORKER_HEADERS_ES = [
  'Nombre completo', 'NIF', 'Nº Seguridad Social', 'Email', 'Teléfono', 'Contraseña',
  'Tipo de usuario', 'Sede',
  'Geolocalización al fichar', 'Trabaja en remoto',
  'Tipo de fichaje',
  'Login habilitado',
  'Provincia', 'Localidad', 'Comunidad autónoma',
  'Convenio',
  ...SCHEDULE_HEADERS_ES,
];

// { 'lunes entrada manana': { dow, slot, label: 'Lunes entrada mañana' }, ... }
export const SCHEDULE_HEADER_MAP = (() => {
  const map = {};
  const slots = ['in1', 'out1', 'in2', 'out2'];
  for (const { value: dow, label } of DAYS_OF_WEEK) {
    const labels = [
      `${label} entrada mañana`, `${label} salida mañana`,
      `${label} entrada tarde`, `${label} salida tarde`,
    ];
    labels.forEach((l, i) => { map[norm(l)] = { dow, slot: slots[i], label: l }; });
  }
  return map;
})();

export const WORKER_HEADER_MAP = normalizeKeys({
  'nombre completo': 'name', 'nombre': 'name',
  'nif': 'nif',
  'nº seguridad social': 'ssNumber', 'n seguridad social': 'ssNumber',
  'numero seguridad social': 'ssNumber', 'seguridad social': 'ssNumber',
  'email': 'email', 'correo': 'email',
  'teléfono': 'phone',
  'contraseña': 'password', 'password': 'password',
  'tipo de usuario': 'type', 'tipo': 'type',
  'sede': 'workplaceName', 'nombre de la sede': 'workplaceName',
  'geolocalización al fichar': 'geoTracking', 'geolocalización': 'geoTracking',
  'trabaja en remoto': 'worksRemotely', 'remoto': 'worksRemotely',
  'tipo de fichaje': 'fichajeMode', 'modo de fichaje': 'fichajeMode', 'modo fichaje': 'fichajeMode',
  // Compatibilidad hacia atrás: plantillas viejas con las 3 columnas separadas
  // se siguen leyendo y se derivan al modo correspondiente.
  'permite fichar en diferido': 'canRequestCheckins', 'fichar en diferido': 'canRequestCheckins', 'fichaje en diferido': 'canRequestCheckins',
  'permite horas extras': 'overtimeAllowed', 'horas extras': 'overtimeAllowed',
  'bloquear fichaje antes hora entrada': 'enforceStartHour',
  'bloquear fichaje antes de la hora de entrada': 'enforceStartHour',
  'enforce start hour': 'enforceStartHour',
  'login habilitado': 'isLoginEnabled',
  'provincia': 'province',
  'localidad': 'locality',
  'comunidad autónoma': 'autonomousCommunity', 'comunidad': 'autonomousCommunity',
  'convenio': 'agreementName',
});

const WORKER_FIELD_LABELS = {
  name: 'Nombre completo',
  nif: 'NIF',
  ssNumber: 'Nº Seguridad Social',
  email: 'Email',
  phone: 'Teléfono',
  password: 'Contraseña',
  type: 'Tipo de usuario',
  workplaceName: 'Sede',
  geoTracking: 'Geolocalización al fichar',
  worksRemotely: 'Trabaja en remoto',
  fichajeMode: 'Tipo de fichaje',
  canRequestCheckins: 'Permite fichar en diferido',
  overtimeAllowed: 'Permite horas extras',
  enforceStartHour: 'Bloquear fichaje antes hora entrada',
  isLoginEnabled: 'Login habilitado',
  province: 'Provincia',
  locality: 'Localidad',
  autonomousCommunity: 'Comunidad autónoma',
  agreementName: 'Convenio',
};

export const WORKER_REQUIRED_FIELDS = ['name', 'email', 'password', 'fichajeMode'];
export const SEDE_REQUIRED_FIELDS = ['name'];
const LEGACY_FICHAJE_FIELDS = ['canRequestCheckins', 'overtimeAllowed', 'enforceStartHour'];

// ─────────────────────────────────────────────────────────────
// Parsers de tipos amigables
// ─────────────────────────────────────────────────────────────
export const parseBool = (v) => {
  if (v === true || v === false) return v;
  if (v == null || v === '') return null;
  const s = norm(v);
  if (['si', 'true', '1', 'yes', 'y', 'x'].includes(s)) return true;
  if (['no', 'false', '0', 'n'].includes(s)) return false;
  return null;
};

const TYPE_BY_LABEL = {};
for (const [k, v] of Object.entries(USER_TYPE_LABELS)) TYPE_BY_LABEL[norm(v)] = Number(k);

// Modos de fichaje aceptados — uno y solo uno por trabajador.
// validation → solo puede firmar dentro de su turno (sin diferido, sin extras)
// free       → puede fichar en diferido y todo lo extra cuenta como horas extra
// mixed      → no puede fichar antes del inicio del turno, pero al final SÍ cuentan horas extra
const FICHAJE_MODE_ALIASES = {
  'validacion de jornada': 'validation', 'validacion': 'validation', 'validation': 'validation',
  'fichaje libre': 'free', 'libre': 'free', 'free': 'free',
  'fichaje mixto': 'mixed', 'mixto': 'mixed', 'mixed': 'mixed',
};
export const parseFichajeMode = (v) => {
  if (v == null || v === '') return null;
  return FICHAJE_MODE_ALIASES[norm(v)] || null;
};
// Dado el modo, devuelve los 3 flags que el backend espera.
export const flagsFromFichajeMode = (mode) => {
  if (mode === 'validation') return { canRequestCheckins: false, overtimeAllowed: false, enforceStartHour: false };
  if (mode === 'free')       return { canRequestCheckins: true,  overtimeAllowed: true,  enforceStartHour: false };
  if (mode === 'mixed')      return { canRequestCheckins: false, overtimeAllowed: true,  enforceStartHour: true  };
  return null;
};
// Camino inverso, solo para plantillas viejas que aún traigan las 3 columnas.
const deduceFichajeModeFromFlags = (cR, oA, eSH) => {
  if (cR === true && oA === true && eSH === false) return 'free';
  if (cR === false && oA === true && eSH === true) return 'mixed';
  if (cR === false && oA === false && eSH === false) return 'validation';
  return null;
};

export const parseType = (v) => {
  if (v == null || v === '') return 0;
  const s = norm(v);
  if (TYPE_BY_LABEL[s] != null) return TYPE_BY_LABEL[s];
  const n = Number(v);
  if (Number.isFinite(n) && n >= 0 && n <= 5) return n;
  return 0;
};

// Acepta CUALQUIER formato de hora que Excel/CSV/usuario pueda meter:
//   · Date object (cellDates:true → celdas Hora vienen aquí).
//   · Number 0..1 (fracción de día) o >= 1 (serial → parte fraccional).
//   · String "9", "9:00", "09:00", "09:00:00", "9.00", "9,00", "9h00",
//     "9 am", "9:00 PM", "21h", "21:30hs"… (los CSV siempre llegan aquí).
// Devuelve "HH:MM" en 24h, o '' si no hay nada reconocible.
export const parseHour = (v) => {
  if (v == null || v === '') return '';

  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const hh = String(v.getHours()).padStart(2, '0');
    const mm = String(v.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
    // Entero 0..23 → hora en punto escrita como número ("9").
    if (Number.isInteger(v) && v >= 0 && v <= 23) {
      return `${String(v).padStart(2, '0')}:00`;
    }
    const frac = v - Math.floor(v);
    const total = Math.round(frac * 86400);
    const hh = String(Math.floor(total / 3600) % 24).padStart(2, '0');
    const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  let s = String(v).trim().toLowerCase();
  if (!s) return '';

  const isPm = /(?:^|[^a-z])p\.?\s*m\.?(?:$|[^a-z])/.test(s);
  const isAm = /(?:^|[^a-z])a\.?\s*m\.?(?:$|[^a-z])/.test(s);

  s = s
    .replace(/[ap]\.?\s*m\.?/g, '')
    .replace(/\bh(rs?|oras?)?\b/g, ':')     // "9h" / "9hrs" / "9horas" → "9:"
    .replace(/[\s ​]+/g, '')      // espacios, NBSP, zero-width
    .replace(/[.,-]/g, ':')                 // 9.00 / 9,00 / 9-00 → 9:00
    .trim();

  let match = s.match(/^(\d{1,2}):*$/);
  if (match) {
    let hh = Number(match[1]);
    if (isPm && hh < 12) hh += 12;
    if (isAm && hh === 12) hh = 0;
    if (hh > 23) return '';
    return `${String(hh).padStart(2, '0')}:00`;
  }

  match = s.match(/^(\d{1,2}):+(\d{1,2})(?::+\d{1,2})?:*$/);
  if (!match) return '';
  let hh = Number(match[1]);
  const mm = Number(match[2]);
  if (Number.isNaN(hh) || Number.isNaN(mm) || mm > 59) return '';
  if (isPm && hh < 12) hh += 12;
  if (isAm && hh === 12) hh = 0;
  if (hh > 23) return '';
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

export const trimStr = (v) => (v == null ? '' : String(v).trim());
const optStr = (v) => trimStr(v) || null;

export const remapRow = (row, headerMap) => {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    const target = headerMap[norm(k)] || null;
    if (target) out[target] = v;
  }
  return out;
};

// Número de fila "tal y como lo ve el usuario en Excel" (cabecera = 1).
const rowNumberOf = (r, i) => (typeof r.__rowNum__ === 'number' ? r.__rowNum__ + 1 : i + 2);

const isBlankRow = (r) => Object.values(r).every((v) => trimStr(v instanceof Date ? 'x' : v) === '');

// Horario semanal de una fila: array de 7 { dayOfWeek, isOff, in1, out1, in2, out2 }.
//   - Las 4 celdas vacías → DESCANSO.
//   - Pares incompletos se ignoran (toBackendDay los trata como descanso).
export const extractWeekFromRow = (row) => DAYS_OF_WEEK.map(({ value: dow }) => {
  const cells = {};
  for (const [k, v] of Object.entries(row)) {
    const def = SCHEDULE_HEADER_MAP[norm(k)];
    if (def && def.dow === dow) cells[def.slot] = v;
  }
  const in1 = parseHour(cells.in1);
  const out1 = parseHour(cells.out1);
  const in2 = parseHour(cells.in2);
  const out2 = parseHour(cells.out2);
  if (!in1 && !out1 && !in2 && !out2) {
    return { dayOfWeek: dow, isOff: true, in1: '', out1: '', in2: '', out2: '' };
  }
  return { dayOfWeek: dow, isOff: false, in1, out1, in2, out2 };
});

// Día local → payload de saveWeekSchedule. Mismo criterio que WorkerModal:
// si solo hay turno tarde, se promueve a in1/out1.
export const toBackendDay = (day) => {
  if (day.isOff) return { dayOfWeek: day.dayOfWeek, isOff: true };
  const has1 = !!day.in1 && !!day.out1;
  const has2 = !!day.in2 && !!day.out2;
  if (has1 && has2) {
    return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in1, out1: day.out1, in2: day.in2, out2: day.out2 };
  }
  if (has1) return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in1, out1: day.out1, in2: null, out2: null };
  if (has2) return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in2, out1: day.out2, in2: null, out2: null };
  return { dayOfWeek: day.dayOfWeek, isOff: true };
};

// null si la contraseña cumple la política; si no, el motivo concreto.
// En alta es obligatoria, así que vacía también es un problema.
export const evaluateRowPassword = (raw) => {
  const pwd = String(raw || '');
  if (!pwd.trim()) {
    return `Contraseña vacía. Es obligatoria para usuarios nuevos (mínimo ${PASSWORD_MIN_LENGTH} caracteres, con mayúscula, minúscula, número y símbolo).`;
  }
  if (!isPasswordValid(pwd)) {
    return firstFailureMessage(pwd) || 'Contraseña no cumple la política de seguridad.';
  }
  return null;
};

// ─────────────────────────────────────────────────────────────
// Filas → entidades
// ─────────────────────────────────────────────────────────────

// Devuelve { sedes, dropped: [{ row, reason }] }.
export const rowsToSedes = (rows) => {
  const sedes = [];
  const dropped = [];
  rows.forEach((r, i) => {
    const _row = rowNumberOf(r, i);
    if (isBlankRow(r)) return;
    const remap = remapRow(r, SEDE_HEADER_MAP);
    if (!trimStr(remap.name)) {
      dropped.push({ row: _row, reason: 'falta «Nombre de la sede»' });
      return;
    }
    sedes.push({
      _row,
      name: trimStr(remap.name),
      email: trimStr(remap.email),
      phone: trimStr(remap.phone),
      isLoginEnabled: parseBool(remap.isLoginEnabled) ?? true,
    });
  });
  return { sedes, dropped };
};

const fichajeFromRow = (r) => {
  const explicit = parseFichajeMode(r.fichajeMode);
  if (explicit) return { fichajeMode: explicit, ...flagsFromFichajeMode(explicit), _fichajeIssue: null };
  const deduced = deduceFichajeModeFromFlags(
    parseBool(r.canRequestCheckins), parseBool(r.overtimeAllowed), parseBool(r.enforceStartHour),
  );
  if (deduced) return { fichajeMode: deduced, ...flagsFromFichajeMode(deduced), _fichajeIssue: null };
  return {
    fichajeMode: null,
    canRequestCheckins: false,
    overtimeAllowed: false,
    enforceStartHour: false,
    _fichajeIssue: 'Falta "Tipo de fichaje" o no es válido. Usa: Validación de jornada, Fichaje libre o Fichaje mixto.',
  };
};

// Devuelve { workers, dropped: [{ row, reason }] }. Las filas sin nombre o
// sin email se descartan, pero se informan (antes se perdían en silencio).
export const rowsToWorkers = (rows) => {
  const workers = [];
  const dropped = [];
  rows.forEach((raw, i) => {
    const _row = rowNumberOf(raw, i);
    if (isBlankRow(raw)) return;
    const r = remapRow(raw, WORKER_HEADER_MAP);
    const missing = [];
    if (!trimStr(r.name)) missing.push('«Nombre completo»');
    if (!trimStr(r.email)) missing.push('«Email»');
    if (missing.length) {
      dropped.push({ row: _row, reason: `falta ${missing.join(' y ')}` });
      return;
    }
    const password = trimStr(r.password);
    workers.push({
      _row,
      name: trimStr(r.name),
      nif: trimStr(r.nif).toUpperCase(),
      ssNumber: optStr(r.ssNumber),
      email: trimStr(r.email),
      phone: trimStr(r.phone),
      password,
      _pwdIssue: evaluateRowPassword(password),
      type: parseType(r.type),
      workplaceName: trimStr(r.workplaceName),
      geoTracking: parseBool(r.geoTracking) ?? false,
      worksRemotely: parseBool(r.worksRemotely) ?? false,
      ...fichajeFromRow(r),
      isLoginEnabled: parseBool(r.isLoginEnabled) ?? true,
      province: optStr(r.province),
      locality: optStr(r.locality),
      autonomousCommunity: optStr(r.autonomousCommunity),
      agreementName: optStr(r.agreementName),
      // La jornada se saca de la fila cruda (el remap solo cubre la ficha).
      week: extractWeekFromRow(raw),
    });
  });
  return { workers, dropped };
};

// ─────────────────────────────────────────────────────────────
// Diagnóstico de cabeceras
// ─────────────────────────────────────────────────────────────
export const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
};

const buildCandidates = (kind) => {
  const map = kind === 'sedes' ? SEDE_HEADER_MAP : WORKER_HEADER_MAP;
  const labels = kind === 'sedes' ? SEDE_FIELD_LABELS : WORKER_FIELD_LABELS;
  const list = Object.entries(map).map(([alias, field]) => ({ alias, field, label: labels[field] }));
  if (kind !== 'sedes') {
    for (const [alias, def] of Object.entries(SCHEDULE_HEADER_MAP)) {
      list.push({ alias, field: `schedule:${alias}`, label: def.label });
    }
  }
  return list;
};

// Sugerencia para una cabecera no reconocida: la cabecera empieza por una
// conocida ("Nombre completo+FA1:AI3" → «Nombre completo») o está a
// distancia de edición ≤2 (≤1 para alias cortos).
const suggestFor = (normalized, candidates) => {
  let best = null;
  for (const c of candidates) {
    if (c.alias.length >= 5 && normalized.startsWith(c.alias)) {
      if (!best || best.kind !== 'prefix' || c.alias.length > best.alias.length) {
        best = { ...c, kind: 'prefix' };
      }
    }
  }
  if (best) return best;
  let bestDist = Infinity;
  for (const c of candidates) {
    const max = c.alias.length >= 5 ? 2 : 1;
    if (Math.abs(c.alias.length - normalized.length) > max) continue;
    const d = levenshtein(normalized, c.alias);
    if (d <= max && d < bestDist) { bestDist = d; best = { ...c, kind: 'typo' }; }
  }
  return best;
};

// kind: 'workers' | 'sedes'.
// Devuelve {
//   recognized: [{ header, field }],
//   unknown: [{ header, suggestion: label|null }],
//   missing: [{ field, label, suggestion: header|null }],   ← BLOQUEANTE
//   wrongFile: string|null,                                 ← BLOQUEANTE
//   blocking: boolean,
// }
export const diagnoseHeaders = (headers, kind) => {
  const map = kind === 'sedes' ? SEDE_HEADER_MAP : WORKER_HEADER_MAP;
  const labels = kind === 'sedes' ? SEDE_FIELD_LABELS : WORKER_FIELD_LABELS;
  const required = kind === 'sedes' ? SEDE_REQUIRED_FIELDS : WORKER_REQUIRED_FIELDS;
  const candidates = buildCandidates(kind);

  const recognized = [];
  const unknown = [];
  const present = new Set();
  for (const header of headers || []) {
    const n = norm(header);
    if (!n) continue;
    const field = map[n] || (kind !== 'sedes' && SCHEDULE_HEADER_MAP[n] ? `schedule:${n}` : null);
    if (field) {
      recognized.push({ header, field });
      present.add(field);
      continue;
    }
    const s = suggestFor(n, candidates);
    unknown.push({ header, suggestion: s ? s.label : null, suggestedField: s ? s.field : null });
  }

  const missing = [];
  for (const field of required) {
    if (present.has(field)) continue;
    // Plantillas viejas: las 3 columnas de flags sustituyen a "Tipo de fichaje".
    if (field === 'fichajeMode' && LEGACY_FICHAJE_FIELDS.every((f) => present.has(f))) continue;
    const near = unknown.find((u) => u.suggestedField === field);
    missing.push({ field, label: labels[field], suggestion: near ? near.header : null });
  }

  // Archivo de trabajadores subido en la caja de sedes: la columna "Sede"
  // se leería como nombre de sede y crearía sedes basura.
  let wrongFile = null;
  if (kind === 'sedes') {
    const ns = (headers || []).map(norm);
    if (ns.includes('nombre completo') || ns.includes('contrasena') || ns.includes('tipo de fichaje')) {
      wrongFile = 'Parece un archivo de TRABAJADORES. Súbelo en la sección «Trabajadores».';
    }
  }

  return {
    recognized,
    unknown: unknown.map(({ header, suggestion }) => ({ header, suggestion })),
    missing,
    wrongFile,
    blocking: missing.length > 0 || !!wrongFile,
  };
};

// ─────────────────────────────────────────────────────────────
// Sedes: resolución de nombres
// ─────────────────────────────────────────────────────────────
export const sedeKey = (name) => norm(name);

// Índice { clave → workplace } de las sedes existentes.
export const indexWorkplaces = (workplaces) => {
  const idx = new Map();
  for (const w of Array.isArray(workplaces) ? workplaces : []) {
    const k = sedeKey(w?.name);
    if (k && !idx.has(k)) idx.set(k, w);
  }
  return idx;
};

// Sedes que usan los trabajadores y que no existen ni vienen en el archivo
// de sedes. Devuelve [{ key, name, rows: [nº fila] }] en orden de aparición.
export const findUnknownSedes = (workers, workplaces, fileSedes = []) => {
  const existing = indexWorkplaces(workplaces);
  const fromFile = new Set((fileSedes || []).map((s) => sedeKey(s.name)));
  const out = new Map();
  for (const t of workers || []) {
    if (!t.workplaceName) continue;
    const k = sedeKey(t.workplaceName);
    if (!k || existing.has(k) || fromFile.has(k)) continue;
    if (!out.has(k)) out.set(k, { key: k, name: t.workplaceName, rows: [] });
    out.get(k).rows.push(t._row);
  }
  return [...out.values()];
};

// Valores del <select> de asignación:
//   ''          → sin decidir (bloquea)
//   'none'      → importar sin sede
//   'wp:<id>'   → sede existente
//   'file:<k>'  → sede del archivo de sedes (se crea antes)
export const unresolvedSedes = (unknown, mapping = {}) =>
  (unknown || []).filter((u) => !mapping[u.key]);

// Resuelve la sede de un trabajador en runImport.
// wpIndex: Map clave → workplace (existentes + recién creadas).
// Devuelve { status: 'none' } | { status: 'ok', id } | { status: 'missing' }.
export const resolveWorkerSede = (workplaceName, wpIndex, mapping = {}) => {
  if (!trimStr(workplaceName)) return { status: 'none' };
  const k = sedeKey(workplaceName);
  const direct = wpIndex.get(k);
  if (direct && direct.id != null) return { status: 'ok', id: String(direct.id) };
  const m = mapping[k];
  if (m === 'none') return { status: 'none' };
  if (typeof m === 'string' && m.startsWith('wp:')) return { status: 'ok', id: m.slice(3) };
  if (typeof m === 'string' && m.startsWith('file:')) {
    const created = wpIndex.get(m.slice(5));
    if (created && created.id != null) return { status: 'ok', id: String(created.id) };
  }
  return { status: 'missing' };
};

// ─────────────────────────────────────────────────────────────
// Duplicados
// ─────────────────────────────────────────────────────────────
const emailKey = (v) => trimStr(v).toLowerCase();
const nifKey = (v) => trimStr(v).toUpperCase().replace(/[\s.-]/g, '');

// existing: { [nº fila]: { field: 'email'|'nif', value } } → se omiten.
// inFile:   [{ field, value, rows }] → BLOQUEANTE (entre filas nuevas).
export const findDuplicates = (workers, existingWorkers) => {
  const byEmail = new Set();
  const byNif = new Set();
  for (const w of Array.isArray(existingWorkers) ? existingWorkers : []) {
    if (emailKey(w?.email)) byEmail.add(emailKey(w.email));
    if (nifKey(w?.nif)) byNif.add(nifKey(w.nif));
  }
  const existing = {};
  const seen = { email: new Map(), nif: new Map() };
  for (const t of workers || []) {
    const e = emailKey(t.email);
    const n = nifKey(t.nif);
    if (e && byEmail.has(e)) { existing[t._row] = { field: 'email', value: t.email }; continue; }
    if (n && byNif.has(n)) { existing[t._row] = { field: 'nif', value: t.nif }; continue; }
    if (e) {
      if (!seen.email.has(e)) seen.email.set(e, { value: t.email, rows: [] });
      seen.email.get(e).rows.push(t._row);
    }
    if (n) {
      if (!seen.nif.has(n)) seen.nif.set(n, { value: t.nif, rows: [] });
      seen.nif.get(n).rows.push(t._row);
    }
  }
  const inFile = [];
  for (const field of ['email', 'nif']) {
    for (const { value, rows } of seen[field].values()) {
      if (rows.length > 1) inFile.push({ field, value, rows });
    }
  }
  return { existing, inFile };
};

// Para el safety-net de runImport: ¿este trabajador ya existe?
export const existingMatch = (t, existingWorkers) => {
  const { existing } = findDuplicates([{ ...t, _row: 0 }], existingWorkers);
  return existing[0] || null;
};
